import { useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const cache = new Map();

export const useDynamicTranslate = () => {
    const { i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const translate = useCallback(async (text, lang = i18n.language) => {
        if (!text || lang === 'en') return text;
        const key = `${text}|${lang}`;
        if (cache.has(key)) {
            return cache.get(key);
        }
        setLoading(true);
        try {
            const { data } = await axios.get("https://translate.googleapis.com/translate_a/single", {
                params: {
                    client: "gtx",
                    sl: "en",
                    tl: lang,
                    dt: "t",
                    q: text
                }
            });

            // const translated = data[0][0][0];
            const translated = data?.[0]
                ?.map(part => part?.[0] || "")
                .join(" ")
                .trim();
            cache.set(key, translated);
            return translated;
        } catch (error) {
            return text;
        } finally {
            setLoading(false);
        }
    }, [i18n.language]);

    const isTranslatableString = (value) => {
        return typeof value === 'string' && value.trim().length > 0;
    };

    const translateDeep = useCallback(async (data, fieldsToTranslate = [], lang = i18n.language) => {
        if (!data) return data;
        if (Array.isArray(data)) {
            return Promise.all(
                data.map(item => translateDeep(item, fieldsToTranslate, lang))
            );
        }
        if (typeof data === 'object') {
            const translated = { ...data };

            for (const fieldPath of fieldsToTranslate) {
                const keys = fieldPath.split('.');
                let current = translated;
                let parent = null;
                let lastKey = null;

                for (let i = 0; i < keys.length - 1; i++) {
                    if (current[keys[i]]) {
                        parent = current;
                        current = current[keys[i]];
                        lastKey = keys[i];
                    } else {
                        break;
                    }
                }

                const finalKey = keys[keys.length - 1];
                if (current && current[finalKey] && isTranslatableString(current[finalKey])) {
                    current[finalKey] = await translate(current[finalKey], lang);
                }
            }

            return translated;
        }

        return data;
    }, [translate]);

    const batchTranslate = useCallback(async (items, fields = ['name'], lang = i18n.language) => {
        if (!Array.isArray(fields)) fields = [fields];
        const isArray = Array.isArray(items);
        const dataArray = isArray ? items : [items];
        const results = await Promise.all(
            dataArray.map(item => translateDeep(item, fields, lang))
        );
        return isArray ? results : results[0];
    }, [translateDeep]);

    const translateAll = useCallback(async (data, lang = i18n.language) => {
        if (!data) return data;
        if (Array.isArray(data)) {
            return Promise.all(
                data.map(item => translateAll(item, lang))
            );
        }
        if (typeof data === 'object') {
            const translated = { ...data };
            for (const [key, value] of Object.entries(data)) {
                if (isTranslatableString(value)) {
                    translated[key] = await translate(value, lang);
                } else if (typeof value === 'object') {
                    translated[key] = await translateAll(value, lang);
                }
            }
            return translated;
        }
        return data;
    }, [translate]);

    return { translate, batchTranslate, translateAll, loading };
};